import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { AnimatePresence } from "framer-motion"
import { SleepRecord } from "@/types/sleep"
import { useSleepRecords } from "./sleep-records/useSleepRecords"
import { SleepTableRow } from "./sleep-records/SleepTableRow"
import { useMediaQuery } from "@/hooks/use-media-query"
import { FileText } from "lucide-react"

export interface SleepRecordsTableProps {
  childId: string
  limit?: number
  records?: SleepRecord[]
  timezone?: string
}

export function SleepRecordsTable({ 
  childId, 
  limit = 10, 
  records, 
  timezone 
}: SleepRecordsTableProps) {
  const { data = [], isLoading, error } = useSleepRecords(childId, limit, records)
  const isMobile = useMediaQuery("(max-width: 479px)")
  const isExtraSmall = useMediaQuery("(max-width: 360px)")
  const isSmall = useMediaQuery("(max-width: 640px)")

  // Log child ID in the component for debugging
  console.log('SleepRecordsTable rendering for child ID:', childId);

  // Display records from props if provided, otherwise use data from query
  const displayData = records || data;

  // Get timezone abbreviation for display
  const userTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneAbbr = userTimezone ? 
    new Intl.DateTimeFormat('en-US', { timeZoneName: 'short', timeZone: userTimezone })
      .formatToParts(new Date())
      .find(part => part.type === 'timeZoneName')?.value || userTimezone
    : 'Local';

  if (isLoading) {
    return (
      <div className="p-3 xs:p-4 text-center text-purple-300/70 animate-pulse">
        Loading sleep records...
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-3 xs:p-4 text-center text-red-400">
        Error: {error.message}
      </div>
    )
  }

  return (
    <div className="rounded-xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-2 xs:p-3 sm:p-4 shadow-xl backdrop-blur-lg border border-white/5 mobile-card">
      <div className="flex flex-col gap-2 xs:gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm xs:text-base sm:text-lg font-semibold text-white">Sleep Records</h3>
          <div className="text-2xs xs:text-xs text-gray-400">
            {isMobile ? timezoneAbbr : `Times in ${timezoneAbbr}`}
          </div>
        </div>
        <div className="overflow-x-auto -mx-1 px-1">
          <div className="rounded-lg overflow-hidden min-w-[280px] xs:min-w-[350px] sm:min-w-0 mobile-table">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-purple-500/20 hover:bg-purple-500/5">
                  <TableHead className="text-2xs xs:text-xs text-purple-300 py-1 xs:py-2 px-1 xs:px-2 sm:px-3 mobile-table-cell">
                    Start Date
                  </TableHead>
                  <TableHead className="text-2xs xs:text-xs text-purple-300 py-1 xs:py-2 px-1 xs:px-2 sm:px-3 mobile-table-cell">
                    End Date
                  </TableHead>
                  <TableHead className="text-2xs xs:text-xs text-purple-300 py-1 xs:py-2 px-1 xs:px-2 sm:px-3 mobile-table-cell">
                    {isExtraSmall ? "Time" : (isMobile ? "Window" : "Sleep Window")}
                  </TableHead>
                  <TableHead className="text-2xs xs:text-xs text-purple-300 py-1 xs:py-2 px-1 xs:px-2 sm:px-3 mobile-table-cell">
                    {isMobile ? "Hrs" : "Duration"}
                  </TableHead>
                  <TableHead className="text-2xs xs:text-xs text-purple-300 py-1 xs:py-2 px-1 xs:px-2 sm:px-3 mobile-table-cell">
                    {isExtraSmall ? "Qlty" : "Quality"}
                  </TableHead>
                  {!isSmall && (
                    <TableHead className="text-2xs xs:text-xs text-purple-300 py-1 xs:py-2 px-1 xs:px-2 sm:px-3 mobile-table-cell">
                      <div className="flex items-center space-x-1">
                        <FileText className="h-3 w-3" />
                        <span>Notes</span>
                      </div>
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                <AnimatePresence>
                  {displayData.map((record) => (
                    <SleepTableRow 
                      key={record.id} 
                      record={record} 
                      timezone={userTimezone} 
                      showNotes={!isSmall}
                    />
                  ))}
                </AnimatePresence>
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  )
}
